import React, { useState, useEffect } from "react";
import { imageUrls } from "../lib/supabase";
import { usePrices } from "../hooks/useContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLungs,
  faStethoscope,
  faNotesMedical,
  faFileMedical,
  faSyringe,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const Prices = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const { prices, loading, error } = usePrices();

  // Add useEffect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get unique categories from prices data
  const getCategories = () => {
    const uniqueCategories = [...new Set(prices.map(price => price.category))];
    
    const categoryIcons = {
      "Consultații": faStethoscope,
      "Investigații": faNotesMedical,
      "Proceduri": faLungs,
      "Terapii": faSyringe,
      "Documente": faFileMedical,
    };

    return [
      { id: "all", name: "Toate serviciile" },
      ...uniqueCategories.map(category => ({
        id: category,
        name: category,
        icon: categoryIcons[category] || faStethoscope
      }))
    ];
  };

  const categories = getCategories();

  // Convert database prices to services format for display
  const convertPricesToServices = () => {
    return prices.map((price, index) => ({
      id: price.id || index + 1,
      name: price.service_name,
      price: typeof price.price === 'number' ? `${price.price} RON` : 
             price.price.includes('RON') ? price.price : `${price.price} RON`,
      img: price.image_url || imageUrls.consultPneumologic(),
      category: price.category,
      icon: getCategoryIcon(price.category),
      description: price.description,
    }));
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      "Consultații": faStethoscope,
      "Investigații": faNotesMedical,
      "Proceduri": faLungs,
      "Terapii": faSyringe,
      "Documente": faFileMedical,
    };
    return categoryIcons[category] || faStethoscope;
  };

  const services = convertPricesToServices();

  const filteredServices =
    activeCategory === "all"
      ? services
      : services.filter((service) => service.category === activeCategory);

  // Handle loading state
  if (loading) {
    return (
      <div className="prices-page">
        <section className="prices-hero">
          <div className="prices-overlay"></div>
          <div className="container">
            <div className="prices-hero-content">
              <h1>Lista de prețuri</h1>
              <p>Servicii medicale de calitate la prețuri competitive</p>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container text-center">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
            <p>Se încarcă prețurile...</p>
          </div>
        </section>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="prices-page">
        <section className="prices-hero">
          <div className="prices-overlay"></div>
          <div className="container">
            <div className="prices-hero-content">
              <h1>Lista de prețuri</h1>
              <p>Servicii medicale de calitate la prețuri competitive</p>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container text-center">
            <div className="alert alert-danger">
              <h4>Eroare la încărcarea prețurilor</h4>
              <p>Ne pare rău, nu am putut încărca lista de prețuri momentan. Vă rugăm să încercați din nou mai târziu.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="prices-page">
      <section className="prices-hero">
        <div className="prices-overlay"></div>
        <div className="container">
          <div className="prices-hero-content">
            <h1>Lista de prețuri</h1>
            <p>Servicii medicale de calitate la prețuri competitive</p>
          </div>
        </div>
      </section>

      <section className="section prices-intro">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="prices-intro-content">
                <h2>Servicii accesibile pentru toți pacienții</h2>
                <p>
                  Bine ați venit la clinica noastră de pneumologie, unde
                  excelența în îngrijirea medicală se întâlnește cu prețuri
                  imbatabile. Echipa noastră dedicată vă oferă servicii de top
                  la tarife accesibile, reflectând angajamentul nostru pentru
                  calitate și accesibilitate.
                </p>
                <p>
                  Descoperiți oferta noastră și alegeți îngrijirea de calitate
                  la prețuri competitive:
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="prices-image-container">
                <img
                  src={imageUrls.pricesSection()}
                  alt="Prețuri servicii medicale"
                  className="img-fluid rounded shadow"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section prices-list">
        <div className="container">
          <div className="prices-categories">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-btn ${
                  activeCategory === category.id ? "active" : ""
                }`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.icon && (
                  <FontAwesomeIcon
                    icon={category.icon}
                    className="category-icon"
                  />
                )}
                {category.name}
              </button>
            ))}
          </div>

          <div className="row g-4 prices-services">
            {filteredServices.map((service, index) => {
              // Check if this is the special package to highlight
              const isFeatured = service.name.toLowerCase().includes('pachet pneumologie') || 
                                service.name.toLowerCase().includes('consult pneumologic');
              
              return (
                <div className="col-lg-4 col-md-6" key={service.id}>
                  <div className={`price-card ${isFeatured ? 'featured' : ''}`}>
                    <div className="price-card-header">
                      <div className="price-card-icon">
                        <FontAwesomeIcon icon={service.icon} />
                      </div>
                    </div>
                    <div className="price-card-content">
                      <h3>{service.name}</h3>
                      {service.description && (
                        <div className="price-card-description">
                          {service.description}
                        </div>
                      )}
                      <div className="price-tag-container">
                        <div className="price-tag">
                          <span>{service.price}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="prices-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Programează o consultație acum</h2>
            <p>
              Contactează-ne pentru a beneficia de serviciile noastre medicale
              de calitate
            </p>
            <a href="/rezervări" className="btn-price-cta">
              Programare online
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Prices;
